<template>
	<div>
		<v-dialog v-model="dialogOpen" max-width="980" persistent scrollable>
			<v-card class="empresa-dialog" outlined>
				<v-card-title class="empresa-dialog__title">
					<div>
						<div>{{ dialogTitle }}</div>
						<div class="empresa-dialog__subtitle">Preencha os dados da empresa em etapas simples.</div>
					</div>
					<v-spacer />
					<v-btn icon :disabled="saving" @click="fecharDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider />

				<v-card-text class="pt-6 pb-0">
					<div class="empresa-steps mb-6">
						<div
							v-for="(step, index) in steps"
							:key="step.value"
							class="empresa-step"
							:class="{
								'empresa-step--active': currentStep === step.value,
								'empresa-step--done': index < currentStepIndex,
							}"
						>
							<div class="empresa-step__number">{{ index + 1 }}</div>
							<div class="empresa-step__content">
								<div class="empresa-step__title">{{ step.title }}</div>
								<div class="empresa-step__caption">{{ step.caption }}</div>
							</div>
						</div>
					</div>

					<v-form ref="empresaForm" v-model="formValid" lazy-validation>
						<div v-show="currentStep === 'identificacao'">
							<v-row dense>
								<v-col cols="12" md="4">
									<v-text-field
										v-model="form.cnpj"
										label="CNPJ"
										outlined
										dense
										:rules="[rules.required]"
										maxlength="18"
										@input="onDocumentoInput('cnpj', $event, 'cnpj')"
									/>
								</v-col>

								<v-col cols="12" md="8">
									<v-text-field
										v-model.trim="form.razao_social"
										label="Razão social"
										outlined
										dense
										:rules="[rules.required]"
									/>
								</v-col>

								<v-col cols="12" md="6">
									<v-file-input
										v-model="form.certificado_a1_arquivo"
										label="Anexar certificado A1"
										outlined
										dense
										show-size
										truncate-length="28"
										accept=".pfx,.p12,application/x-pkcs12"
										prepend-icon="mdi-file-certificate-outline"
										:rules="[rules.certificado]"
										@change="onCertificadoChange"
									/>
									<div v-if="certificadoAtualLabel" class="empresa-field-caption">
										Arquivo selecionado: {{ certificadoAtualLabel }}
									</div>
								</v-col>

								<v-col cols="12" md="6">
									<v-text-field
										v-model.trim="form.certificado_a1_senha"
										label="Senha do certificado A1"
										outlined
										dense
										type="password"
										:rules="[rules.required]"
									/>
								</v-col>

								<v-col cols="12" md="6">
									<v-text-field
										v-model.trim="form.nome_fantasia"
										label="Nome fantasia"
										outlined
										dense
										:rules="[rules.required]"
									/>
								</v-col>

								<v-col cols="12" md="3">
									<v-text-field
										v-model.trim="form.inscricao_estadual"
										label="Inscrição estadual"
										outlined
										dense
									/>
								</v-col>

								<v-col cols="12" md="3">
									<v-text-field
										v-model.trim="form.inscricao_municipal"
										label="Inscrição municipal"
										outlined
										dense
									/>
								</v-col>

								<v-col cols="12" md="12">
									<v-select
										v-model="form.regime_tributario"
										:items="regimesTributarios"
										label="Regime tributário"
										outlined
										dense
										:rules="[rules.required]"
									/>
								</v-col>
							</v-row>
						</div>

						<div v-show="currentStep === 'contato'">
							<v-row dense>
								<v-col cols="12" md="6">
									<v-text-field
										v-model.trim="form.email"
										label="E-mail"
										outlined
										dense
										:rules="[rules.required, rules.email]"
									/>
								</v-col>

								<v-col cols="12" md="6">
									<v-text-field
										v-model="form.telefone"
										label="Telefone"
										outlined
										dense
										:rules="[rules.required]"
										maxlength="15"
										@input="onDocumentoInput('telefone', $event, 'telefone')"
									/>
								</v-col>
							</v-row>
						</div>

						<div v-show="currentStep === 'responsavel'">
							<v-row dense>
								<v-col cols="12" md="8">
									<v-text-field
										v-model.trim="form.nome_responsavel"
										label="Nome responsável"
										outlined
										dense
										:rules="[rules.required]"
									/>
								</v-col>

								<v-col cols="12" md="4">
									<v-text-field
										v-model="form.cpf_responsavel"
										label="CPF responsável"
										outlined
										dense
										:rules="[rules.required]"
										maxlength="14"
										@input="onDocumentoInput('cpf_responsavel', $event, 'cpf')"
									/>
								</v-col>
							</v-row>
						</div>

						<div v-show="currentStep === 'endereco'">
							<v-row dense>
								<v-col cols="12" md="3">
									<v-text-field
										v-model="form.cep"
										label="CEP"
										outlined
										dense
										:rules="[rules.required]"
										maxlength="9"
										@input="onDocumentoInput('cep', $event, 'cep')"
									/>
								</v-col>

								<v-col cols="12" md="7">
									<v-text-field
										v-model.trim="form.logradouro"
										label="Logradouro"
										outlined
										dense
										:rules="[rules.required]"
									/>
								</v-col>

								<v-col cols="12" md="2">
									<v-text-field
										v-model.trim="form.numero"
										label="Número"
										outlined
										dense
										:rules="[rules.required]"
									/>
								</v-col>

								<v-col cols="12" md="4">
									<v-text-field
										v-model.trim="form.complemento"
										label="Complemento"
										outlined
										dense
									/>
								</v-col>

								<v-col cols="12" md="4">
									<v-text-field
										v-model.trim="form.bairro"
										label="Bairro"
										outlined
										dense
										:rules="[rules.required]"
									/>
								</v-col>

								<v-col cols="12" md="4">
									<v-text-field
										v-model.trim="form.municipio"
										label="Município"
										outlined
										dense
										:rules="[rules.required]"
									/>
								</v-col>

								<v-col cols="12" md="4">
									<v-select
										v-model="form.uf"
										:items="ufs"
										label="UF"
										outlined
										dense
										:rules="[rules.required]"
									/>
								</v-col>
							</v-row>
						</div>
					</v-form>
				</v-card-text>

				<v-card-actions class="px-6 pb-6 pt-4">
					<v-btn text :disabled="saving || currentStepIndex === 0" @click="voltarStep">Voltar</v-btn>
					<v-spacer />
					<v-btn text :disabled="saving" @click="fecharDialog">Cancelar</v-btn>
					<v-btn
						v-if="!isLastStep"
						class="empresa-save-btn"
						depressed
						:disabled="saving"
						@click="avancarStep"
					>
						Próxima etapa
					</v-btn>
					<v-btn
						v-else
						class="empresa-save-btn"
						depressed
						:loading="saving"
						:disabled="saving"
						@click="salvarEmpresa"
					>
						{{ saveButtonLabel }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
const REGIMES_TRIBUTARIOS = [
	'Simples Nacional',
	'Simples Nacional - excesso de sublimite de receita bruta',
	'Regime normal',
	'Simples Nacional - microempreendedor individual - MEI',
];

const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

const STEPS = [
	{ value: 'identificacao', title: 'Identificação', caption: 'Dados fiscais e certificado' },
	{ value: 'contato', title: 'Contato', caption: 'E-mail e telefone' },
	{ value: 'responsavel', title: 'Responsável', caption: 'Dados do responsável' },
	{ value: 'endereco', title: 'Endereço', caption: 'Localização da empresa' },
];

function createInitialForm() {
	return {
		cnpj: '',
		razao_social: '',
		certificado_a1_arquivo: null,
		certificado_a1_base64: '',
		certificado_a1_nome_arquivo: '',
		certificado_a1_senha: '',
		nome_fantasia: '',
		inscricao_estadual: '',
		inscricao_municipal: '',
		regime_tributario: '',
		email: '',
		telefone: '',
		nome_responsavel: '',
		cpf_responsavel: '',
		cep: '',
		logradouro: '',
		numero: '',
		complemento: '',
		bairro: '',
		municipio: '',
		uf: '',
	};
}

function readFileAsBase64(file) {
	return new Promise((resolve, reject) => {
		if (!file) {
			resolve('');
			return;
		}

		const reader = new FileReader();

		reader.onload = () => {
			const result = String(reader.result || '');
			const [, base64 = ''] = result.split(',');
			resolve(base64);
		};

		reader.onerror = () => {
			reject(new Error('Nao foi possivel ler o certificado A1.'));
		};

		reader.readAsDataURL(file);
	});
}

function onlyDigits(value) {
	return String(value || '').replace(/\D/g, '');
}

function applyMask(value, type) {
	const digits = onlyDigits(value);

	if (type === 'cnpj') {
		return digits
			.slice(0, 14)
			.replace(/^(\d{2})(\d)/, '$1.$2')
			.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
			.replace(/\.(\d{3})(\d)/, '.$1/$2')
			.replace(/(\/\d{4})(\d)/, '$1-$2');
	}

	if (type === 'cpf') {
		return digits
			.slice(0, 11)
			.replace(/^(\d{3})(\d)/, '$1.$2')
			.replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
			.replace(/\.(\d{3})(\d)/, '.$1-$2');
	}

	if (type === 'telefone') {
		const limited = digits.slice(0, 11);
		if (limited.length <= 10) {
			return limited
				.replace(/^(\d{2})(\d)/, '($1) $2')
				.replace(/(\d{4})(\d)/, '$1-$2');
		}

		return limited
			.replace(/^(\d{2})(\d)/, '($1) $2')
			.replace(/(\d{5})(\d)/, '$1-$2');
	}

	if (type === 'cep') {
		return digits.slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2');
	}

	return value;
}

export default {
	name: 'CadastroEmpresaDialog',

	props: {
		value: {
			type: Boolean,
			default: false,
		},
		empresa: {
			type: Object,
			default: null,
		},
	},

	data() {
		return {
			dialogOpen: false,
			formValid: false,
			saving: false,
			currentStep: STEPS[0].value,
			steps: STEPS,
			regimesTributarios: REGIMES_TRIBUTARIOS,
			ufs: UFS,
			form: createInitialForm(),
			rules: {
				required: (value) => Boolean(String(value || '').trim()) || 'Campo obrigatório',
				email: (value) => !value || /.+@.+\..+/.test(String(value).trim()) || 'Informe um e-mail válido',
				certificado: () => Boolean(String(this.certificadoAtualLabel || '').trim()) || 'Anexe o certificado A1',
			},
		};
	},

	computed: {
		isEditMode() {
			return Boolean(this.empresa?.id);
		},

		dialogTitle() {
			return this.isEditMode ? 'Editar empresa' : 'Nova empresa';
		},

		saveButtonLabel() {
			return this.isEditMode ? 'Salvar alterações' : 'Cadastrar empresa';
		},

		currentStepIndex() {
			return this.steps.findIndex((step) => step.value === this.currentStep);
		},

		isLastStep() {
			return this.currentStepIndex === this.steps.length - 1;
		},

		certificadoAtualLabel() {
			if (this.form.certificado_a1_arquivo?.name) {
				return this.form.certificado_a1_arquivo.name;
			}

			return this.form.certificado_a1_nome_arquivo || '';
		},
	},

	watch: {
		value: {
			immediate: true,
			handler(newValue) {
				this.dialogOpen = newValue;
			},
		},

		dialogOpen(newValue) {
			this.$emit('input', newValue);

			if (newValue) {
				this.preencherFormulario();
				return;
			}

			if (!this.saving) {
				this.resetForm();
			}
		},

		empresa: {
			deep: true,
			handler() {
				if (this.dialogOpen) {
					this.preencherFormulario();
				}
			},
		},
	},

	methods: {
		onDocumentoInput(field, value, type) {
			this.form[field] = applyMask(value, type);
		},

		async onCertificadoChange(file) {
			this.form.certificado_a1_arquivo = file || null;

			if (!file?.name) {
				this.form.certificado_a1_base64 = '';
				this.form.certificado_a1_nome_arquivo = '';
				return;
			}

			this.form.certificado_a1_nome_arquivo = file.name;

			try {
				this.form.certificado_a1_base64 = await readFileAsBase64(file);
			} catch (error) {
				this.form.certificado_a1_arquivo = null;
				this.form.certificado_a1_base64 = '';
				this.form.certificado_a1_nome_arquivo = '';
				this.$toasted?.error(error.message || 'Nao foi possivel processar o certificado A1.');
			}
		},

		preencherFormulario() {
			if (!this.empresa) {
				this.form = createInitialForm();
				this.currentStep = this.steps[0].value;
				return;
			}

			this.form = {
				...createInitialForm(),
				...this.empresa,
				certificado_a1_arquivo: null,
				certificado_a1_base64: '',
				certificado_a1_nome_arquivo: this.empresa.certificado_a1_nome_arquivo || '',
			};
			this.currentStep = this.steps[0].value;
		},

		resetForm() {
			this.form = createInitialForm();
			this.formValid = false;
			this.currentStep = this.steps[0].value;
			this.$nextTick(() => {
				this.$refs?.empresaForm?.resetValidation?.();
			});
		},

		fecharDialog() {
			if (!this.saving) {
				this.dialogOpen = false;
			}
		},

		voltarStep() {
			if (this.currentStepIndex <= 0) {
				return;
			}

			this.currentStep = this.steps[this.currentStepIndex - 1].value;
		},

		avancarStep() {
			if (!this.validarStepAtual()) {
				return;
			}

			if (!this.isLastStep) {
				this.currentStep = this.steps[this.currentStepIndex + 1].value;
			}
		},

		validarStepAtual() {
			const requiredMap = {
				identificacao: ['cnpj', 'razao_social', 'certificado_a1_senha', 'nome_fantasia', 'regime_tributario'],
				contato: ['email', 'telefone'],
				responsavel: ['nome_responsavel', 'cpf_responsavel'],
				endereco: ['cep', 'logradouro', 'numero', 'bairro', 'municipio', 'uf'],
			};

			const requiredFields = requiredMap[this.currentStep] || [];
			const missingField = requiredFields.find((field) => !String(this.form[field] || '').trim());

			if (this.currentStep === 'identificacao' && !this.certificadoAtualLabel) {
				this.$toasted?.error('Anexe o certificado A1 para continuar.');
				return false;
			}

			if (missingField) {
				this.$toasted?.error('Preencha os campos obrigatórios da etapa atual.');
				return false;
			}

			if (this.currentStep === 'contato' && this.form.email && !/.+@.+\..+/.test(String(this.form.email).trim())) {
				this.$toasted?.error('Informe um e-mail válido.');
				return false;
			}

			return true;
		},

		async salvarEmpresa() {
			if (!this.validarStepAtual()) {
				return;
			}

			if (this.isEditMode && !this.form.certificado_a1_base64) {
				this.currentStep = 'identificacao';
				this.$toasted?.error('Anexe novamente o certificado A1 para atualizar a empresa na Focus.');
				return;
			}

			this.saving = true;

			try {
				if (!this.form.certificado_a1_base64 && this.form.certificado_a1_arquivo) {
					this.form.certificado_a1_base64 = await readFileAsBase64(this.form.certificado_a1_arquivo);
				}

				const payload = {
					cnpj: this.form.cnpj,
					razao_social: this.form.razao_social,
					certificado_a1_base64: this.form.certificado_a1_base64 || null,
					certificado_a1_nome_arquivo: this.form.certificado_a1_nome_arquivo || '',
					certificado_a1_senha: this.form.certificado_a1_senha,
					nome_fantasia: this.form.nome_fantasia,
					inscricao_estadual: this.form.inscricao_estadual || null,
					inscricao_municipal: this.form.inscricao_municipal || null,
					regime_tributario: this.form.regime_tributario,
					email: this.form.email,
					telefone: this.form.telefone,
					nome_responsavel: this.form.nome_responsavel,
					cpf_responsavel: this.form.cpf_responsavel,
					cep: this.form.cep,
					logradouro: this.form.logradouro,
					numero: this.form.numero,
					complemento: this.form.complemento || null,
					bairro: this.form.bairro,
					municipio: this.form.municipio,
					uf: this.form.uf,
				};

				this.$emit('save', payload);
			} finally {
				this.saving = false;
			}
		},
	},
};
</script>

<style scoped>
@import "../../assets/inter-font.css";

.empresa-dialog {
	border-radius: 24px !important;
	border: 1px solid #e6ebf1 !important;
	box-shadow: 0 24px 70px rgba(15, 23, 42, 0.12) !important;
	font-family: "Inter", sans-serif;
}

.empresa-dialog__title {
	padding: 24px 24px 18px;
	font-size: 20px;
	font-weight: 700;
	color: #1f2a44;
}

.empresa-dialog__subtitle {
	font-size: 13px;
	font-weight: 500;
	color: #7a8ca6;
	margin-top: 4px;
}

.empresa-steps {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 12px;
}

.empresa-step {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 14px 16px;
	border-radius: 16px;
	border: 1px solid #dce8e4;
	background: #f8fbff;
	transition: all 0.2s ease;
}

.empresa-step--active {
	border-color: #12b57a;
	background: #ecfdf5;
	box-shadow: 0 12px 24px rgba(18, 181, 122, 0.12);
}

.empresa-step--done {
	border-color: #12b57a;
	background: #f0fdf4;
}

.empresa-step__number {
	width: 34px;
	height: 34px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 999px;
	background: #ffffff;
	color: #12b57a;
	font-weight: 800;
	flex-shrink: 0;
	border: 1px solid #dce8e4;
}

.empresa-step__title {
	font-size: 14px;
	font-weight: 700;
	color: #1f2a44;
}

.empresa-step__caption {
	font-size: 12px;
	color: #7a8ca6;
	margin-top: 2px;
	line-height: 1.4;
}

.empresa-field-caption {
	font-size: 12px;
	color: #6b7a90;
	margin-top: -8px;
}

.empresa-save-btn {
	background: linear-gradient(135deg, #18c58b 0%, #12b57a 100%) !important;
	color: #fff !important;
	font-weight: 700;
	border-radius: 12px;
	text-transform: none;
	padding: 0 18px !important;
	box-shadow: 0 12px 28px rgba(18, 181, 122, 0.22) !important;
}

@media (max-width: 960px) {
	.empresa-steps {
		grid-template-columns: 1fr 1fr;
	}
}

@media (max-width: 600px) {
	.empresa-steps {
		grid-template-columns: 1fr;
	}
}
</style>